import { useEffect, useMemo, useRef } from "react";
import { useGuestLogs } from "@/providers/GuestLogs";
import { useGuestCompanionsOptional } from "@/providers/GuestCompanions";
import { COMPANION_SCOPE } from "@/types/enums/CompanionScope";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { parseToLocalDate } from "@/lib/utils/parser";
import { GuestLog } from "@/types/GuestLog";

/**
 * The host's timeline, with its companions' timelines merged in.
 *
 * A companion is its OWN guest row (`guestType: companion`, `mainGuest: <host>`),
 * so every one of its events — COMPANION_ADDED, status moves, a door scan —
 * is written against the companion's `guestId`. Read by guest id, the host's
 * history therefore never mentions its own party, and answering "what happened
 * to this guest" meant opening every companion in turn.
 *
 * Merging happens HERE and not server-side because `guest_logs.companionId` is
 * dead in both directions: no producer in any repo ever writes it, and it is on
 * neither `GuestLogService.ALLOWED_GUEST_LOG_FIELDS` nor `convertToDTO`. The
 * renderer, though, has always been ready for it — and expects a NAME, not an
 * id (`useDocumentation`'s "DELETED" sentinel; the pink chip in
 * `getStatusColor`). Feeding that from the client costs no write contract and
 * works RETROACTIVELY on every row already in the database.
 *
 * Two gaps follow from "only who still exists and is visible can be merged",
 * and both are deliberate:
 *   - a REMOVED companion is gone: `GuestService.createDeleteLogs` writes
 *     COMPANION_REMOVED against the companion's own id and then deletes the
 *     guest, so `getCompanions` can never find it again;
 *   - a DECLINED companion is filtered out upstream
 *     (`GuestRepository.nonDeclinedCompanionPredicate`). PENDING is not.
 *     The companions TAB of this same dialog hides declined rows too, and two
 *     disagreeing companion lists on one screen would be the worse defect.
 */

/** The sentinel `useDocumentation` already renders as `t("unknown")`. */
const UNKNOWN_COMPANION = "DELETED";

/**
 * `GuestLog.createdAt` is typed `Date` but is a STRING for everything that came
 * off the wire — `GuestLogClient.getGuestLogs` hands the JSON through untouched
 * and only `createGuestLog` converts. So the freshly added note carries a real
 * Date while every fetched row carries a string, and both land in ONE list here.
 * Anything unparseable sorts to the bottom rather than poisoning the comparator
 * with NaN, which would make the order incoherent instead of merely wrong.
 */
function timeOf(value: GuestLog["createdAt"]): number {
  const date = value instanceof Date ? value : parseToLocalDate(String(value ?? ""));
  const time = date ? date.getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
}

type MergedTimeline = {
  entries: GuestLog[];
  loading: boolean;
  /** The HOST's own read error. A companion's failure never lands here. */
  errorMessage: string | null;
  /**
   * How many companions' histories are missing from `entries` because their
   * read failed. A merged audit trail that silently drops rows is worse than
   * one that admits it is partial, so the surface says so.
   */
  incompleteCompanions: number;
};

export function useMergedGuestTimeline(hostGuestId: string): MergedTimeline {
  const { allLogs, loadErrors, fetchLogs } = useGuestLogs();
  // The Companions TAB is gated on this capability (GuestNotes); the Doku tab
  // needs only `guests:read`. Asking anyway is a guaranteed 403 on every open
  // for such a role — and `fetchCompanions` caches `[]` in its catch behind a
  // permanent early-return, so that one 403 also hides the companions tab's
  // real content for the rest of the session.
  //
  // Deliberately the THROWING hook, not `useCapabilitiesOptional`: that one
  // falls back to "no capabilities at all", which here would silently drop
  // every companion from the history — the exact failure class this file
  // exists to prevent. `GuestNotes`, the only thing that mounts this timeline,
  // already calls `useCapabilities` itself, so a missing provider is a broken
  // mount that should say so loudly.
  const { can } = useCapabilities();
  const mayReadCompanions = can("guests:read.companions");
  // Optional on purpose (same shape as TCCCompanions): all three mount points
  // pair the two providers, so this resolves in practice — but a timeline that
  // hard-throws because a future host forgot one provider is a bad trade for a
  // decoration.
  const companionsCtx = useGuestCompanionsOptional();
  const fetchCompanions = companionsCtx?.fetchCompanions;
  const allCompanions = companionsCtx?.allCompanions;
  // A failed COMPANION LIST is the more likely failure of the two, and it used
  // to be invisible: the provider cached `[]` on error behind a permanent early
  // return, so `companions` read as "this guest has none" and
  // `incompleteCompanions` stayed 0 — the timeline claimed completeness it did
  // not have. The provider now leaves the key absent and records the error.
  const companionsFailed = !!companionsCtx?.loadErrors?.has(hostGuestId);

  // Own dedupe, for two independent reasons:
  //  - `fetchCompanions` depends on `allCompanionsState`, so its identity
  //    changes on every companion mutation and this effect re-runs;
  //  - `fetchLogs` adds to its own loadedRef only AFTER the await, so two
  //    concurrent calls for one id both get through its guard.
  const requestedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    fetchLogs(hostGuestId);
  }, [hostGuestId, fetchLogs]);

  useEffect(() => {
    if (!fetchCompanions || !mayReadCompanions) return;
    // EVERY companion row, including the declined and archived ones.
    //
    // This is a HISTORY. The companions panel next door shows who is in the
    // party and hides what was removed — the right answer for a list you act
    // on, and the wrong one here: a merged audit trail that silently drops the
    // rows somebody removed is the failure this file's own `incompleteCompanions`
    // exists to admit to. (The provider remembers which scope each cached list
    // was loaded with, so the two tabs no longer decide for each other.)
    fetchCompanions(hostGuestId, false, COMPANION_SCOPE.ALL);
  }, [hostGuestId, fetchCompanions, mayReadCompanions]);

  const companions = mayReadCompanions
    ? (allCompanions?.get(hostGuestId) ?? null)
    : null;

  useEffect(() => {
    if (!companions) return;
    for (const companion of companions) {
      const companionId = companion.guestId;
      if (!companionId || companionId === hostGuestId) continue;
      if (requestedRef.current.has(companionId)) continue;
      requestedRef.current.add(companionId);
      void fetchLogs(companionId);
    }
  }, [companions, hostGuestId, fetchLogs]);

  // The provider's map is `Map<string, GuestLog[] | null>`, so three states are
  // reachable: absent (never fetched), null, and an array. Only ABSENT means
  // "still loading" — a stored null is a loaded-but-empty timeline.
  const hostEntry = allLogs.get(hostGuestId);
  const hostLoaded = hostEntry !== undefined;
  const hostRows = useMemo(() => hostEntry ?? [], [hostEntry]);

  const entries = useMemo(() => {
    const merged: GuestLog[] = [];

    for (const row of hostRows) {
      // Defensive blank: the server never fills `companionId` today, and the
      // renderer prints it as a name. Should that ever change, an unblanked
      // hex id would render itself into the sentence.
      merged.push(row.companionId === undefined ? row : { ...row, companionId: undefined });
    }

    for (const companion of companions ?? []) {
      const companionId = companion.guestId;
      if (!companionId || companionId === hostGuestId) continue;
      const rows = allLogs.get(companionId);
      if (!rows) continue;
      const name = [companion.firstName, companion.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const label = name || UNKNOWN_COMPANION;
      for (const row of rows) merged.push({ ...row, companionId: label });
    }

    merged.sort((a, b) => {
      const byTime = timeOf(b.createdAt) - timeOf(a.createdAt);
      if (byTime !== 0) return byTime;
      // A batch create stamps one `now` across the host row and every
      // companion row, so ties are the common case, not the exotic one. The id
      // is the total order that keeps React keys from shuffling between renders.
      return (b.guestLogId ?? "").localeCompare(a.guestLogId ?? "");
    });

    return merged;
  }, [hostRows, companions, allLogs, hostGuestId]);

  const incompleteCompanions = useMemo(() => {
    let n = 0;
    for (const companion of companions ?? []) {
      const companionId = companion.guestId;
      if (!companionId || companionId === hostGuestId) continue;
      if (loadErrors.has(companionId)) n += 1;
    }
    // A list we could not load is at least one unknown companion.
    return companionsFailed ? Math.max(n, 1) : n;
  }, [companions, hostGuestId, loadErrors, companionsFailed]);

  return {
    entries,
    // Readiness is per guest. The provider no longer carries a global `loading`
    // at all — it ended in `finally { setLoading(false) }` per fetch, so with
    // N+1 timelines in flight the first one home switched the spinner off for
    // everyone.
    loading: !hostLoaded,
    // The HOST's own error, keyed. The previous shape read a single global
    // string, which every companion fetch cleared on entry — so a failed host
    // read was erased one round trip later and the outage rendered as an empty
    // history (or, once companion rows arrived, as a complete-looking timeline
    // containing only someone else's events).
    errorMessage: loadErrors.get(hostGuestId) ?? null,
    incompleteCompanions,
  };
}
